import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

const PARENT_ORIGINS_ENV = 'https://ertcrefund.com'

const ALLOWED_PARENT_ORIGINS = PARENT_ORIGINS_ENV
  .split(',')
  .map((origin) => origin.trim())
  .filter(Boolean)

const HEIGHT_MESSAGE_TYPE = 'ertc:height'
const ROUTE_CHANGE_REPOST_DELAY_MS = 50

/**
 * Resolves the parent window's origin from document.referrer and
 * checks it against the configured allowlist. Returns an empty
 * string if the parent is unknown, untrusted, or unparsable.
 *
 * @returns {String}
 */
const resolveTrustedParentOrigin = () => {
  if (!document.referrer) return ''
  try {
    const referrerOrigin = new URL(document.referrer).origin
    return ALLOWED_PARENT_ORIGINS.includes(referrerOrigin) ? referrerOrigin : ''
  } catch {
    return ''
  }
}

/**
 * Auto-resizes the iframe to the natural height of its content by
 * posting messages to the parent window. The parent listens for
 * { type: 'ertc:height', height } and sets the iframe element's
 * height. With internal scrolling disabled inside the iframe, the
 * parent page becomes the only scroll surface — no nested scroll.
 *
 * Fail-closed: if not iframed, or the parent origin is not in the
 * REACT_APP_IFRAME_PARENT_ORIGINS allowlist, this hook posts nothing.
 *
 * @returns {{ isIframed: Boolean }}
 */
export default function useIframeAutoResize() {
  const location = useLocation()
  const [isIframed, setIsIframed] = useState(false)
  const lastHeightRef = useRef(0)
  const parentOriginRef = useRef('')

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window === window.parent) return

    setIsIframed(true)

    const parentOrigin = resolveTrustedParentOrigin()
    parentOriginRef.current = parentOrigin
    if (!parentOrigin) {
      if (process.env.NODE_ENV !== 'production')
        console.warn('[useIframeAutoResize] parent origin not in allowlist; skipping height postMessage')
      return
    }

    const postHeight = () => {
      const nextHeight = Math.ceil(document.documentElement.scrollHeight)
      if (nextHeight === lastHeightRef.current) return
      lastHeightRef.current = nextHeight
      window.parent.postMessage(
        { type: HEIGHT_MESSAGE_TYPE, height: nextHeight },
        parentOrigin
      )
    }

    const observer = new ResizeObserver(postHeight)
    observer.observe(document.documentElement)
    window.addEventListener('load', postHeight)

    postHeight()

    return () => {
      observer.disconnect()
      window.removeEventListener('load', postHeight)
    }
  }, [])

  useEffect(() => {
    if (!isIframed) return
    const parentOrigin = parentOriginRef.current
    if (!parentOrigin) return

    const timeoutId = setTimeout(() => {
      const nextHeight = Math.ceil(document.documentElement.scrollHeight)
      lastHeightRef.current = nextHeight
      window.parent.postMessage(
        { type: HEIGHT_MESSAGE_TYPE, height: nextHeight },
        parentOrigin
      )
    }, ROUTE_CHANGE_REPOST_DELAY_MS)

    return () => clearTimeout(timeoutId)
  }, [location.pathname, isIframed])

  return { isIframed }
}
